/**
 * Centralized error handling system
 * Provides consistent error management across the application
 */

import { logger } from './logger';

// Base error class
export class AppError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly isOperational: boolean;
  public readonly context?: Record<string, any>;

  constructor(
    message: string,
    code: string,
    statusCode: number = 500,
    isOperational: boolean = true,
    context?: Record<string, any>
  ) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.statusCode = statusCode;
    this.isOperational = isOperational;
    this.context = context;

    Error.captureStackTrace(this, this.constructor);
  }
}

// Specific error types
export class ValidationError extends AppError {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'VALIDATION_ERROR', 400, true, context);
  }
}

export class AuthenticationError extends AppError {
  constructor(message: string = 'Authentication required', context?: Record<string, any>) {
    super(message, 'AUTHENTICATION_ERROR', 401, true, context);
  }
}

export class AuthorizationError extends AppError {
  constructor(message: string = 'Insufficient permissions', context?: Record<string, any>) {
    super(message, 'AUTHORIZATION_ERROR', 403, true, context);
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string = 'Resource', context?: Record<string, any>) {
    super(`${resource} not found`, 'NOT_FOUND_ERROR', 404, true, context);
  }
}

export class ConflictError extends AppError {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'CONFLICT_ERROR', 409, true, context);
  }
}

export class NetworkError extends AppError {
  constructor(message: string = 'Network request failed', context?: Record<string, any>) {
    super(message, 'NETWORK_ERROR', 0, true, context);
  }
}

export class SupabaseError extends AppError {
  constructor(message: string, originalError?: any, context?: Record<string, any>) {
    const enhancedContext = {
      ...context,
      originalError: originalError?.message || originalError,
      supabaseCode: originalError?.code,
      supabaseDetails: originalError?.details
    };
    
    super(message, 'SUPABASE_ERROR', 500, true, enhancedContext);
  }
}

// Error handler utility
export class ErrorHandler {
  static handle(error: Error | AppError, context?: Record<string, any>): AppError {
    // If it's already an AppError, just log and return
    if (error instanceof AppError) {
      logger.error(error.message, {
        ...context,
        code: error.code,
        statusCode: error.statusCode,
        ...error.context
      }, error);
      return error;
    }

    // Handle Supabase errors
    if (this.isSupabaseError(error)) {
      const supabaseError = new SupabaseError(
        this.getSupabaseErrorMessage(error),
        error,
        context
      );
      logger.error(supabaseError.message, supabaseError.context, supabaseError);
      return supabaseError;
    }

    // Handle network errors
    if (this.isNetworkError(error)) {
      const networkError = new NetworkError(error.message, context);
      logger.error(networkError.message, networkError.context, networkError);
      return networkError;
    }

    // Generic error handling
    const appError = new AppError(
      error.message || 'An unexpected error occurred',
      'UNKNOWN_ERROR',
      500,
      false,
      context
    );
    
    logger.error(appError.message, appError.context, appError);
    return appError;
  }

  static isSupabaseError(error: any): boolean {
    return error?.name === 'PostgrestError' || 
           error?.name === 'AuthError' ||
           error?.message?.includes('supabase') ||
           error?.code?.startsWith('PGRST');
  }

  static isNetworkError(error: any): boolean {
    return error?.name === 'NetworkError' ||
           error?.code === 'NETWORK_ERROR' ||
           error?.message?.includes('fetch') ||
           error?.message?.includes('network');
  }

  static getSupabaseErrorMessage(error: any): string {
    if (error?.message) {
      // Common Supabase error mappings
      if (error.message.includes('duplicate key')) {
        return 'Cette ressource existe déjà';
      }
      if (error.message.includes('foreign key')) {
        return 'Impossible de supprimer cette ressource car elle est utilisée ailleurs';
      }
      if (error.message.includes('not found')) {
        return 'Ressource introuvable';
      }
      if (error.message.includes('permission denied')) {
        return 'Permissions insuffisantes pour cette action';
      }
      if (error.message.includes('invalid input')) {
        return 'Données invalides fournies';
      }
      
      return error.message;
    }
    return 'Erreur de base de données';
  }

  // User-friendly error messages for UI
  static getUserMessage(error: AppError): string {
    switch (error.code) {
      case 'VALIDATION_ERROR':
        return error.message; // Validation errors are usually user-friendly
      case 'AUTHENTICATION_ERROR':
        return 'Veuillez vous connecter pour continuer';
      case 'AUTHORIZATION_ERROR':
        return 'Vous n\'avez pas les permissions nécessaires';
      case 'NOT_FOUND_ERROR':
        return error.message;
      case 'CONFLICT_ERROR':
        return error.message;
      case 'NETWORK_ERROR':
        return 'Problème de connexion. Veuillez réessayer.';
      case 'SUPABASE_ERROR':
        return this.getSupabaseErrorMessage(error);
      default:
        return 'Une erreur inattendue s\'est produite';
    }
  }

  // Get appropriate toast/notification type
  static getNotificationType(error: AppError): 'error' | 'warning' | 'info' {
    switch (error.code) {
      case 'VALIDATION_ERROR':
        return 'warning';
      case 'NOT_FOUND_ERROR':
        return 'info';
      default:
        return 'error';
    }
  }
}

// Hook for React components
export const useErrorHandler = () => {
  const handleError = (error: Error | AppError, context?: Record<string, any>) => {
    return ErrorHandler.handle(error, context);
  };

  const getUserMessage = (error: AppError) => {
    return ErrorHandler.getUserMessage(error);
  };

  const getNotificationType = (error: AppError) => {
    return ErrorHandler.getNotificationType(error);
  };

  return {
    handleError,
    getUserMessage,
    getNotificationType
  };
};

// Utility functions for common patterns
export const withErrorHandling = <T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  context?: Record<string, any>
) => {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (_error) {
      throw ErrorHandler.handle(_error as Error, context);
    }
  };
};

export const safeAsync = <T>(
  promise: Promise<T>,
  context?: Record<string, any>
): Promise<[AppError | null, T | null]> => {
  return promise
    .then((data: T) => [null, data] as [null, T])
    .catch((error: Error) => [ErrorHandler.handle(error, context), null] as [AppError, null]);
};